import React, { useState, useEffect } from "react";
import Card from "../shared/Card";
import Table from "../shared/Table";
import SearchBar from "../shared/SearchBar";
import Pagination from "../shared/Pagination";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";

const API_URL = import.meta.env.VITE_API_URL;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [hideInternalUsers, setHideInternalUsers] = useState(false);
  const [usersWithProjects, setUsersWithProjects] = useState({}); // Store project info for each user
  const [loadedBatches, setLoadedBatches] = useState(new Set()); // Track which batches are loaded
  const [loadingBatch, setLoadingBatch] = useState(false); // Track if currently loading a batch
  const [powerUsers, setPowerUsers] = useState([]); // Store original power users data
  const [filteredPowerUsers, setFilteredPowerUsers] = useState([]); // Store filtered power users data
  const [showPowerUsers, setShowPowerUsers] = useState(false); // Toggle power users view
  const [powerUsersLoaded, setPowerUsersLoaded] = useState(false); // Track if power users data is loaded
  const [powerUsersPage, setPowerUsersPage] = useState(1); // Current page for power users
  const [powerUsersPerPage] = useState(10); // Users per page for power users
  const navigate = useNavigate();
  const { admin, token } = useSelector((state) => state.auth);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedUsers, setPaginatedUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]); // Users actually shown (with project data)
  const usersPerPage = 10;

  // Update available subscription plans to match the actual plans
  const subscriptionPlans = ["Freemium", "Pro", "Team", "Enterprise"];

  useEffect(() => {
    fetchUsers();
  }, [token]); // Add token as dependency

  useEffect(() => {
    applyFilters();
    if (showPowerUsers && powerUsers.length > 0) {
      applyPowerUsersFilters();
    }
  }, [searchQuery, selectedPlan, startDate, endDate, hideInternalUsers, users, showPowerUsers, powerUsers]);
  
  // Update pagination whenever filtered users change
  useEffect(() => {
    handlePageDisplay();
  }, [filteredUsers, currentPage]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Configure headers with auth token
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Fetch users
      const usersResponse = await axios.get(`${API_URL}/api/users`, config);
      const usersData = usersResponse.data;

      // Fetch subscriptions for all users
      const subscriptionsResponse = await axios.get(`${API_URL}/api/subscriptions`, config);
      const subscriptionsData = subscriptionsResponse.data;

      // Fetch all plans
      const plansResponse = await axios.get(`${API_URL}/api/plans`, config);
      const plansData = plansResponse.data;

      // Create a map of plans for quick lookup
      const plansMap = plansData.reduce((acc, plan) => {
        acc[plan._id] = plan;
        return acc;
      }, {});

      // Create a map of subscriptions by user ID
      const subscriptionsByUser = subscriptionsData.reduce((acc, sub) => {
        if (sub.status === 'active') {
          acc[sub.user] = sub;
        }
        return acc;
      }, {});

      // Combine user data with their subscription plan and sort by creation date
      const usersWithPlans = usersData.map(user => {
        const userSubscription = subscriptionsByUser[user._id];
        const userPlan = userSubscription ? plansMap[userSubscription.plan] : null;
        return {
          ...user,
          subscriptionPlan: userPlan ? userPlan.name : 'Freemium', // Default to Freemium if no plan found
          planDetails: userPlan || null,
          isInternal: user.isInternal || false // Ensure isInternal field is available
        };
      }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by createdAt in descending order

      setUsers(usersWithPlans);
      setFilteredUsers(usersWithPlans);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else {
        setError("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  };

  const isInternalEmail = (email) => {
    if (!email) return false;
    const internalDomains = ['mammoth-ai.com', 'bigentities.com'];
    const emailDomain = email.split('@')[1];
    return internalDomains.includes(emailDomain);
  };

  const getBaseEmail = (email) => {
    if (!email) return '';
    return email.replace(/\+[0-9]+@/, '@');
  };
  const hasPlusInEmail = (email) => {
    if (!email) return false;
    return email.includes('+');
  };
  // Combined function to apply all filters together
  const applyFilters = () => {
    let filtered = [...users];
    
    // Apply text search if exists
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query)
      );
    }
    
    // Apply plan filter if selected
    if (selectedPlan) {
      filtered = filtered.filter(
        (user) => user.subscriptionPlan === selectedPlan
      );
    }
    
    // Apply date range filter if both dates are set
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Set end date to end of day for inclusive search
      end.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter((user) => {
        const createdDate = new Date(user.createdAt);
        return createdDate >= start && createdDate <= end;
      });
    }
    if (hideInternalUsers) {
      // First filter out users marked as internal in the database
      filtered = filtered.filter(user => !user.isInternal);
      
      // Then apply the legacy email domain filtering for backward compatibility
      filtered = filtered.filter(user => !isInternalEmail(user.email));
      filtered = filtered.filter(user => !hasPlusInEmail(user.email));
      const baseEmailMap = new Map();
      const uniqueUsers = [];
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      filtered.forEach(user => {
        const baseEmail = getBaseEmail(user.email);
        if (!baseEmailMap.has(baseEmail)) {
          baseEmailMap.set(baseEmail, true);
          uniqueUsers.push(user);
        }
      });
      filtered = uniqueUsers;
    }

    // Sort filtered results by creation date (newest first)
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when applying filters
    // Clear loaded batches when filters change
    setLoadedBatches(new Set());
    setUsersWithProjects({});
    setDisplayedUsers([]);
  };

  // Apply filters to power users
  const applyPowerUsersFilters = () => {
    if (!powerUsers.length) return;
    
    let filtered = [...powerUsers];
    
    // Apply text search if exists
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query)
      );
    }
    
    // Apply plan filter if selected
    if (selectedPlan) {
      filtered = filtered.filter(
        (user) => user.subscriptionPlan === selectedPlan
      );
    }
    
    // Apply date range filter if both dates are set
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter((user) => {
        const createdDate = new Date(user.createdAt);
        return createdDate >= start && createdDate <= end;
      });
    }
    
    // Apply internal user filter
    if (hideInternalUsers) {
      filtered = filtered.filter(user => !user.isInternal);
      filtered = filtered.filter(user => !isInternalEmail(user.email));
      filtered = filtered.filter(user => !hasPlusInEmail(user.email));
    }
    
    // Sort by power score (highest to lowest)
    filtered.sort((a, b) => b.powerScore - a.powerScore);
    
    setFilteredPowerUsers(filtered);
    setPowerUsersPage(1); // Reset to first page when applying filters
  };

  // Handle what users to display based on current page and loaded batches
  const handlePageDisplay = async () => {
    const total = Math.ceil(filteredUsers.length / usersPerPage);
    setTotalPages(total === 0 ? 1 : total);
    
    const batchKey = `${currentPage}`;
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const currentPageUsers = filteredUsers.slice(startIndex, endIndex);
    
    // Update paginatedUsers for display count
    setPaginatedUsers(currentPageUsers);
    
    // If this batch is already loaded, show it immediately
    if (loadedBatches.has(batchKey)) {
      setDisplayedUsers(currentPageUsers);
      return;
    }
    
    // If not loaded, we need to load project data for this batch first
    if (currentPageUsers.length > 0 && !loadingBatch) {
      await loadBatchProjectData(currentPageUsers, batchKey);
    } else {
      setDisplayedUsers(currentPageUsers);
    }
  };

  // Load project data for a specific batch of users
  const loadBatchProjectData = async (batchUsers, batchKey) => {
    try {
      setLoadingBatch(true);
      
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      const projectsStatus = { ...usersWithProjects };
      
      // Fetch project details for current batch users
      const userDetailsPromises = batchUsers.map(async (user) => {
        try {
          const response = await axios.get(`${API_URL}/api/users/${user._id}`, config);
          const userDetail = response.data;
          
          // Check if user has any projects (website creation)
          const hasWebsite = userDetail.projects && userDetail.projects.length > 0;
          
          projectsStatus[user._id] = {
            hasWebsite,
            projectCount: userDetail.projects ? userDetail.projects.length : 0,
            projects: userDetail.projects || []
          };
        } catch (error) {
          console.error(`Error fetching details for user ${user._id}:`, error);
          // If error, assume no website created
          projectsStatus[user._id] = {
            hasWebsite: false,
            projectCount: 0,
            projects: []
          };
        }
      });

      // Wait for all users in this batch to complete
      await Promise.all(userDetailsPromises);
      
      // Update states after successful batch load
      setUsersWithProjects(projectsStatus);
      setLoadedBatches(prev => new Set([...prev, batchKey]));
      setDisplayedUsers(batchUsers);
      
    } catch (error) {
      console.error("Error loading batch project data:", error);
      setError("Failed to load user project data");
      // Still display users even if project data fails
      setDisplayedUsers(batchUsers);
    } finally {
      setLoadingBatch(false);
    }
  };
  
  // Handle page change - only allow if not currently loading
  const handlePageChange = (page) => {
    if (!loadingBatch) {
      setCurrentPage(page);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Handle plan selection
  const handlePlanChange = (plan) => {
    setSelectedPlan(plan);
  };

  const handleInternalUserToggle = () => {
    setHideInternalUsers(!hideInternalUsers);
  };
  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedPlan("");
    setStartDate("");
    setEndDate("");
    setHideInternalUsers(false);
    setShowPowerUsers(false);
    setPowerUsersLoaded(false);
    setPowerUsersPage(1);
    setPowerUsers([]); // Clear power users when clearing filters
    setFilteredPowerUsers([]); // Clear filtered power users when clearing filters
    setFilteredUsers(users);
    setCurrentPage(1);
    setLoadedBatches(new Set());
    setUsersWithProjects({});
    setDisplayedUsers([]);
  };

  const handleDateSearch = () => {
    if (!startDate || !endDate) {
      return;
    }
    
    // We no longer need to do anything special here since
    // the main applyFilters function handles dates too
    // Just ensure we have valid dates and let the effect handle the rest
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        throw new Error("Invalid date");
      }
      // applyFilters will be triggered by the dependency array in useEffect
    } catch (error) {
      console.error("Error with date format:", error);
      setError("Invalid date format");
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      const response = await axios.get(`${API_URL}/api/users/${userId}`, config);
      const data = response.data;
      navigate(`/users/${userId}`, { state: { user: data } });
    } catch (error) {
      console.error("Error fetching user details:", error);
      if (error.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else {
        setError("Failed to load user details");
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to format date in dd/mm/yyyy format
  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Function to calculate cost for Fullstack projects (same as UserDetailPage)
  const calculateFullstackCost = (inputTokens, outputTokens) => {
    const input = inputTokens || 0;
    const output = outputTokens || 0;
    return (input * 0.000003) + (output * 0.000015);
  };

  // Function to calculate total cost for a user's projects
  const calculateUserTotalCost = (projects) => {
    if (!projects || projects.length === 0) return 0;
    
    return projects.reduce((sum, project) => {
      let projectCost = Number(project.price) || 0;
      
      // Add calculated cost for Fullstack projects
      if (project.type === 'Fullstack') {
        const fullstackCost = calculateFullstackCost(
          project.tokens?.input_tokens,
          project.tokens?.output_tokens
        );
        projectCost += fullstackCost;
      }
      
      return sum + projectCost;
    }, 0);
  };

  // Function to format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  // Function to fetch power users data from optimized endpoint
  const fetchPowerUsers = async () => {
    try {
      setLoadingBatch(true);
      setError(null);
      
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      const response = await axios.get(`${API_URL}/api/users/power-users`, config);
      const powerUsersData = response.data;
      
      setPowerUsers(powerUsersData);
      setFilteredPowerUsers(powerUsersData);
      setPowerUsersLoaded(true);
    } catch (error) {
      console.error("Error fetching power users:", error);
      if (error.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else {
        setError("Failed to load power users");
      }
    } finally {
      setLoadingBatch(false);
    }
  };

  // Load power users data when checkbox is checked
  useEffect(() => {
    if (showPowerUsers && !powerUsersLoaded) {
      fetchPowerUsers();
    }
  }, [showPowerUsers, powerUsersLoaded]);

  // Calculate power users pagination
  const getPowerUsersPagination = () => {
    const totalPages = Math.ceil(filteredPowerUsers.length / powerUsersPerPage);
    const startIndex = (powerUsersPage - 1) * powerUsersPerPage;
    const endIndex = startIndex + powerUsersPerPage;
    const paginatedPowerUsers = filteredPowerUsers.slice(startIndex, endIndex);
    
    return {
      totalPages,
      paginatedPowerUsers,
      startIndex,
      endIndex
    };
  };

  // Handle power users page change
  const handlePowerUsersPageChange = (page) => {
    setPowerUsersPage(page);
  };

  // Function to convert user data to CSV format
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    // CSV header - Added Website Created, Contact Number, Promo, Total Projects, and Total Cost columns
    const header = ["Full Name", "Email", "Contact Number", "Created At", "Plan", "Promo Used", "Campaign Promo", "Total Projects", "Total Cost", "Website Created", "ID"];

    // CSV rows
    const rows = data.map((item) => {
      // For power users, data comes directly from API
      if (showPowerUsers) {
        return [
          item.fullName || "",
          item.email || "",
          item.contactNumber || "N/A",
          formatDate(item.createdAt),
          item.subscriptionPlan || "No Plan",
          item.hasUsedPromo ? "Yes" : "No",
          item.isCampaignPromo ? "Yes" : "No",
          item.projectCount || 0,
          formatPrice(item.totalCost || 0),
          item.hasWebsite ? "Yes" : "No",
          item._id || "",
        ];
      }

      // For regular users, use existing logic
      const projectInfo = usersWithProjects[item._id] || { hasWebsite: false, projectCount: 0 };
      const totalCost = calculateUserTotalCost(projectInfo.projects);

      return [
        item.fullName || "",
        item.email || "",
        item.contactNumber || "N/A",
        formatDate(item.createdAt),
        item.subscriptionPlan || "No Plan",
        item.hasUsedPromo ? "Yes" : "No",
        item.isCampaignPromo ? "Yes" : "No",
        projectInfo.projectCount || 0,
        formatPrice(totalCost),
        projectInfo.hasWebsite ? "Yes" : "No",
        item._id || "",
      ];
    });

    // Combine header and rows
    const csvContent = [
      header.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    return csvContent;
  };

  // Function to download CSV file - Load all project data for all filtered users
  const handleExportCSV = async () => {
    try {
      setLoading(true);
      
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Get all users that don't have project data yet
      const usersNeedingData = filteredUsers.filter(user => !usersWithProjects[user._id]);
      
      if (usersNeedingData.length > 0) {
        // Fetch project data for all filtered users for complete CSV export
        const projectsStatus = { ...usersWithProjects };
        
        // Process in batches of 10 for better performance
        const batchSize = 10;
        for (let i = 0; i < usersNeedingData.length; i += batchSize) {
          const batch = usersNeedingData.slice(i, i + batchSize);
          
          const userDetailsPromises = batch.map(async (user) => {
            try {
              const response = await axios.get(`${API_URL}/api/users/${user._id}`, config);
              const userDetail = response.data;
              
              const hasWebsite = userDetail.projects && userDetail.projects.length > 0;
              
              projectsStatus[user._id] = {
                hasWebsite,
                projectCount: userDetail.projects ? userDetail.projects.length : 0,
                projects: userDetail.projects || []
              };
            } catch (error) {
              console.error(`Error fetching details for user ${user._id}:`, error);
              projectsStatus[user._id] = {
                hasWebsite: false,
                projectCount: 0,
                projects: []
              };
            }
          });

          await Promise.all(userDetailsPromises);
        }
        
        setUsersWithProjects(projectsStatus);
      }

      const csvData = convertToCSV(showPowerUsers ? filteredPowerUsers : filteredUsers);
      if (!csvData) {
        alert("No data to export");
        return;
      }

      // Create a Blob with the CSV data
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

      // Create a download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Set link properties
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `users-export-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      // Add link to document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      setError("Failed to export CSV");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      header: "Full Name",
      accessor: "fullName",
    },
    {
      header: "Email",
      accessor: "email",
    },
    {
      header: "Contact Number",
      accessor: "contactNumber",
      Cell: ({ value }) => (
        <span className="text-gray-900">
          {value || "N/A"}
        </span>
      ),
    },
    {
      header: "Created At",
      accessor: "createdAt",
      Cell: ({ value }) => formatDate(value),
    },
    {
      header: "Plan/Promo",
      accessor: "planPromo",
      Cell: ({ row }) => {
        const plan = row.original.subscriptionPlan;
        const hasUsedPromo = row.original.hasUsedPromo;

        return (
          <div className="flex flex-col items-center space-y-1">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                plan === "Freemium"
                  ? "bg-gray-100 text-gray-800"
                  : plan === "Pro"
                  ? "bg-indigo-100 text-indigo-800"
                  : plan === "Enterprise"
                  ? "bg-purple-100 text-purple-800"
                  : plan === "Team"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {plan}
            </span>
            <div className="flex items-center space-x-1">
              <span className="text-xs text-gray-600">Promo:</span>
              {hasUsedPromo ? (
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  title="Used Promo"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  title="No Promo Used"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: "Campaign Promo",
      accessor: "isCampaignPromo",
      Cell: ({ row }) => {
        const isCampaignPromo = row.original.isCampaignPromo;

        return (
          <div className="flex items-center justify-center">
            {isCampaignPromo ? (
              <svg
                className="w-5 h-5 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                title="Campaign Promo"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                title="No Campaign Promo"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </div>
        );
      },
    },
    {
      header: "Total Projects",
      accessor: "projectCount",
      Cell: ({ row }) => {
        // For power users, data comes directly from API
        if (showPowerUsers) {
          return (
            <span className="text-gray-900 font-medium">
              {row.original.projectCount || 0}
            </span>
          );
        }

        // For regular users, use existing logic
        const userId = row.original._id;
        const projectInfo = usersWithProjects[userId];
        const currentBatchKey = `${currentPage}`;
        const isBatchLoaded = loadedBatches.has(currentBatchKey);

        // Show loading if batch is being loaded
        if (loadingBatch || !isBatchLoaded) {
          return (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          );
        }

        // Show loading if specific user data is not available
        if (!projectInfo) {
          return (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          );
        }

        return (
          <span className="text-gray-900 font-medium">
            {projectInfo.projectCount || 0}
          </span>
        );
      },
    },
    {
      header: "Total Cost",
      accessor: "totalCost",
      Cell: ({ row }) => {
        // For power users, data comes directly from API
        if (showPowerUsers) {
          return (
            <span className="text-gray-900 font-medium">
              {formatPrice(row.original.totalCost || 0)}
            </span>
          );
        }

        // For regular users, use existing logic
        const userId = row.original._id;
        const projectInfo = usersWithProjects[userId];
        const currentBatchKey = `${currentPage}`;
        const isBatchLoaded = loadedBatches.has(currentBatchKey);

        // Show loading if batch is being loaded
        if (loadingBatch || !isBatchLoaded) {
          return (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          );
        }

        // Show loading if specific user data is not available
        if (!projectInfo) {
          return (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          );
        }

        const totalCost = calculateUserTotalCost(projectInfo.projects);
        return (
          <span className="text-gray-900 font-medium">
            {formatPrice(totalCost)}
          </span>
        );
      },
    },
    {
      header: "Website Created",
      accessor: "websiteStatus",
      Cell: ({ row }) => {
        // For power users, data comes directly from API
        if (showPowerUsers) {
          return (
            <div className="flex items-center justify-center">
              {row.original.hasWebsite ? (
                <svg 
                  className="w-5 h-5 text-green-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  title="Website created"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg 
                  className="w-5 h-5 text-red-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  title="No website created"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
          );
        }

        // For regular users, use existing logic
        const userId = row.original._id;
        const projectInfo = usersWithProjects[userId];
        const currentBatchKey = `${currentPage}`;
        const isBatchLoaded = loadedBatches.has(currentBatchKey);

        // Show loading if batch is being loaded
        if (loadingBatch || !isBatchLoaded) {
          return (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          );
        }

        // Show loading if specific user data is not available
        if (!projectInfo) {
          return (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-center">
            {projectInfo.hasWebsite ? (
              <svg 
                className="w-5 h-5 text-green-500" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                title="Website created"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg 
                className="w-5 h-5 text-red-500" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                title="No website created"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </div>
        );
      },
    },
    {
      header: "ID",
      accessor: "_id",
      Cell: ({ value }) => (
        <div className="relative group">
          <span className="cursor-pointer" title={value}>
            {value.slice(0, 8)}...
          </span>
        </div>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <>
          {admin.role === "admin" && (
            <button
              onClick={() => fetchUserDetails(row.original._id)}
              className="text-indigo-600 hover:text-indigo-900 font-medium"
            >
              View
            </button>
          )}
        </>
      ),
    },
  ];

  // Create final columns array based on view mode
  const finalColumns = showPowerUsers 
    ? columns.filter(col => col.header !== "Plan/Promo" && col.header !== "Website Created")
    : columns;

  if (error) {
    return <div className="text-center py-4 text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 mt-16">Users</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center mt-16"
            disabled={(!filteredUsers || filteredUsers.length === 0) && (!filteredPowerUsers || filteredPowerUsers.length === 0) || loading}
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            {loading ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      <Card>
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
            <div className="w-full md:w-3/4">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                onSearch={handleSearch}
                placeholder="Search by name or email..."
              />
            </div>
            
            <div className="w-full md:w-1/4 mt-4 md:mt-0">
              <div className="relative">
                <select
                  value={selectedPlan}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg
                          border-[#E6CCB2] bg-white/80 backdrop-blur-sm
                          text-gray-900 placeholder-gray-500
                          focus:outline-none focus:ring-2 focus:ring-[#B85C38]
                          transition-all duration-300 ease-in-out appearance-none"
                >
                  <option value="">All Plans</option>
                  {subscriptionPlans.map((plan) => (
                    <option key={plan} value={plan}>
                      {plan}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4 text-[#9B7E6B]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium text-[#9B7E6B] mb-1 ">
                Start Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg
                          border-[#E6CCB2] bg-white/80 backdrop-blur-sm
                          text-gray-900 placeholder-gray-500
                          focus:outline-none focus:ring-2 focus:ring-[#B85C38]
                          transition-all duration-300 ease-in-out"
                />
                {startDate && (
                  <button
                    onClick={() => setStartDate("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5 mr-10 text-[#9B7E6B]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="w-full md:w-1/2 mt-4 md:mt-0">
              <label className="block text-sm font-medium text-[#9B7E6B] mb-1">
                End Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={endDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg
                          border-[#E6CCB2] bg-white/80 backdrop-blur-sm
                          text-gray-900 placeholder-gray-500
                          focus:outline-none focus:ring-2 focus:ring-[#B85C38]
                          transition-all duration-300 ease-in-out"
                />
                {endDate && (
                  <button
                    onClick={() => setEndDate("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5 mr-10"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideInternalUsers}
                  onChange={handleInternalUserToggle}
                  className="w-4 h-4 text-[#B85C38] bg-white border-[#E6CCB2] rounded focus:ring-[#B85C38] focus:ring-2"
                />
                <span className="text-sm font-medium text-[#9B7E6B]">
                  Hide Internal Users
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showPowerUsers}
                  onChange={() => setShowPowerUsers(!showPowerUsers)}
                  className="w-4 h-4 text-purple-600 bg-white border-purple-300 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-[#9B7E6B]">
                  Show Power Users
                </span>
              </label>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-[#b85c38] text-white rounded-lg hover:bg-[#B85C38]/90 transition-colors"
                disabled={!searchQuery && !selectedPlan && !startDate && !endDate && !hideInternalUsers && !showPowerUsers}
              >
                Clear All Filters
              </button>
              
              <button
                onClick={handleDateSearch}
                className="px-4 py-2 bg-[#b85c38] text-white rounded-lg hover:bg-[#B85C38]/90 transition-colors"
                disabled={!startDate || !endDate}
              >
                Search Date Range
              </button>
            </div>
          </div>

          {/* Show loading message when loading initial data or batch */}
          {(loading && !displayedUsers.length) || loadingBatch ? (
            <div className="text-center py-4">
              {loading ? "Loading users..." : showPowerUsers ? "Loading power users..." : "Loading user details..."}
            </div>
          ) : (
            <Table
              columns={finalColumns}
              data={showPowerUsers ? getPowerUsersPagination().paginatedPowerUsers : displayedUsers}
              emptyMessage={showPowerUsers ? "No power users found" : "No users found"}
            />
          )}
          
          {(filteredUsers.length > 0 || filteredPowerUsers.length > 0) && (
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {showPowerUsers ? (
                  <>
                    Showing {getPowerUsersPagination().startIndex + 1}-{Math.min(getPowerUsersPagination().endIndex, filteredPowerUsers.length)} of {filteredPowerUsers.length} power users (sorted by power score)
                    {loadingBatch && <span className="ml-2 text-blue-600">(Loading...)</span>}
                  </>
                ) : (
                  <>
                    Showing {Math.min(usersPerPage, paginatedUsers.length)} of {filteredUsers.length} users
                    {loadingBatch && <span className="ml-2 text-blue-600">(Loading...)</span>}
                  </>
                )}
              </div>
              {showPowerUsers ? (
                <Pagination
                  currentPage={powerUsersPage}
                  totalPages={getPowerUsersPagination().totalPages}
                  onPageChange={handlePowerUsersPageChange}
                  disabled={loadingBatch}
                />
              ) : (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={loadingBatch} // Disable pagination while loading
                />
              )}
            </div>
          )}
        </div>
      </Card>

      {selectedUser && (
        <UserModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
};

export default Users;